import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { POINTS } from "@nifgash/shared/types";
import { Button } from "../components/Button";
import { MascotScene } from "../components/Mascot";
import { tg } from "../lib/telegram";

const PERKS = [
  { icon: "🎯", key: "create" },
  { icon: "🔍", key: "filters" },
  { icon: "⭐", key: "boost" },
] as const;

/**
 * Shown once, right after a user completes registration (the backend awards
 * the signup bonus at that moment). Explains the welcome points and that they
 * cover exactly one month of Premium — teaching the points→premium loop on day
 * one. Reached via navigate("/welcome-bonus") from Register on first completion.
 */
export function WelcomeBonus() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const app = tg();
    app?.BackButton.hide();
    app?.MainButton.hide();
  }, []);

  return (
    <div className="min-h-screen bg-surface flex flex-col">
      <div className="relative overflow-hidden pt-10 pb-8 px-5 text-center">
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              "radial-gradient(ellipse 90% 60% at 50% 0%, var(--color-brand-yellow-soft) 0%, transparent 70%)",
          }}
        />
        <div className="flex justify-center mb-2">
          <MascotScene width={260} />
        </div>
        <div className="text-[14px] font-bold uppercase tracking-[0.08em] text-ink-500">
          {t("welcomeBonus.kicker")}
        </div>
        <div className="text-[64px] font-extrabold text-ink-900 leading-none mt-2 tracking-tight">
          +{POINTS.SIGNUP_BONUS}
        </div>
        <div className="text-[16px] font-semibold text-brand-purple mt-1">
          {t("welcomeBonus.pointsLabel")}
        </div>
        <p className="text-[15px] text-ink-600 mt-4 leading-relaxed max-w-[330px] mx-auto">
          {t("welcomeBonus.explainMonth")}
        </p>
      </div>

      <div className="px-5">
        <div className="bg-ink-50 rounded-[var(--radius-lg)] p-4 space-y-3">
          {PERKS.map((p) => (
            <div key={p.key} className="flex items-center gap-3">
              <div className="flex-shrink-0 w-9 h-9 rounded-full bg-surface flex items-center justify-center text-[18px]">
                {p.icon}
              </div>
              <div className="text-[14px] text-ink-700 font-medium">
                {t(`welcomeBonus.perks.${p.key}`)}
              </div>
            </div>
          ))}
        </div>
        <p className="text-[13px] text-ink-500 mt-4 text-center leading-relaxed max-w-[330px] mx-auto">
          {t("welcomeBonus.howToEarn")}
        </p>
      </div>

      <div className="px-5 mt-auto pt-8 pb-10 space-y-3">
        <Button stretched size="lg" onClick={() => navigate("/premium", { replace: true })}>
          {t("welcomeBonus.activateCta")}
        </Button>
        <Button stretched size="lg" variant="secondary" onClick={() => navigate("/events", { replace: true })}>
          {t("welcomeBonus.laterCta")}
        </Button>
      </div>
    </div>
  );
}
